
import ColorNoteItem from '@/components/timetable/ColorNoteItem.vue'
import TimeTableTable from '@/components/timetable/TimeTableTable.vue'
import ToPDFRender from '@/components/timetable/ToPDFRender.vue'
import {
  ACKNOWNLEDGE_TIMETABLE,
  DELETE_TIME_TABLE,
  IMPORT_TIMETABLE_CSV,
  LOAD_TIMETABLE,
  OPEN_DIALOG,
  UPDATE_TIMETABLE_TIME,
} from '@/store/actions'
import { TIMETABLE_DATETIME } from '@/utils/constants'
import { ETimeTableState } from '@/utils/enums'
import errorHandler from '@/utils/errorHandler'
import { isEmpty } from '@/utils/helpers'
import { convertStringToDate } from '@/utils/timeUtils'
import { IDialogMessage, ITimeTable, ITimetableUpdateNode } from '@/utils/types'
import cloneDeep from 'lodash/cloneDeep'
import { maska } from 'maska'
import moment from 'moment-timezone'
import { Options, Vue } from 'vue-class-component'
import { Watch } from 'vue-property-decorator'

@Options({
  components: {
    ColorNoteItem,
    TimeTableTable,
    ToPDFRender,
  },
  directives: { maska },
  name: 'TimeTable',
})
export default class TimeTable extends Vue {
  loading = false
  uploadedProgress = 0

  selectedDateObject = new Date() as Date

  actionButton = ''
  acknowledgePayload = {} as ITimetableUpdateNode
  isTriggerExportPDF = false

  dialog: IDialogMessage = {
    isVisible: false,
    title: '',
    content: '',
    buttonContent: '',
  }

  get selectedDate() {
    return this.$store.state.timetable.selectedDate
  }

  get timetable(): ITimeTable {
    return this.$store.state.timetable.timetable
  }

  get stateAcked() {
    return ETimeTableState.ACKED
  }

  get stateTracked() {
    return ETimeTableState.TRACKED
  }

  get stateDelayed() {
    return ETimeTableState.DELAYED
  }

  get stateScheduled() {
    return ETimeTableState.SCHEDULED
  }

  get hasTimeTable() {
    return !isEmpty(this.timetable)
  }

  get maxStopPointNumber() {
    let number = 0
    if (this.timetable?.schedules) {
      Object.values(this.timetable.schedules).forEach((element) => {
        if (element.paths) {
          const numberItem = element.paths.length * 2
          if (number < numberItem) {
            number = numberItem
          }
        }
      })
    }
    return number
  }

  exportPDFDone() {
    this.isTriggerExportPDF = false
  }

  onExportPDF() {
    this.isTriggerExportPDF = true
  }

  @Watch('selectedDateObject')
  selectedDateChanged(value: Date) {
    const newTime = moment(value)
    const selectedDate = newTime.format(TIMETABLE_DATETIME.DATE_PARAM_FORMAT)
    this.reloadData(selectedDate)
  }

  onPickCsvFile() {
    this.$refs.fileCsvInput.click()
  }

  async onCsvFilePicked(firstCsvFile: Blob) {
    this.loading = true
    try {
      const newDate = await this.$store.dispatch(IMPORT_TIMETABLE_CSV, { file: firstCsvFile })
      this.$store.dispatch(OPEN_DIALOG, {
        message: this.$t('timetable.import_success'),
        needtranslation: true,
      })

      if (newDate) {
        this.selectedDateObject = convertStringToDate(newDate)
      }
    } catch (error) {
      if (error !== '2306') {
        errorHandler(error)
      }
    } finally {
      this.loading = false
    }

    this.$refs.fileCsvInput.value = null
  }

  async reloadData(selectedDate: string) {
    this.loading = true
    try {
      await this.$store.dispatch(LOAD_TIMETABLE, { date: selectedDate })
    } catch (error) {
      if (error !== '2306') {
        errorHandler(error)
      }
    } finally {
      this.loading = false
    }
  }

  confirmDialog() {
    try {
      switch (this.actionButton) {
        case 'delete':
          this.deleteTimeTable()
          break
        case 'updateStopPoint':
          this.onUpdateStopPoint(this.acknowledgePayload)
          break
        default:
          break
      }
    } catch (error) {
      errorHandler(error)
    } finally {
      this.clearDialogData()
    }
  }

  async deleteTimeTable() {
    this.loading = true
    try {
      await this.$store.dispatch(DELETE_TIME_TABLE, {
        id: this.timetable.id,
        selectedDate: this.selectedDate,
      })
    } catch (error) {
      if (error !== '2306') {
        errorHandler(error)
      }
    } finally {
      this.loading = false
    }
  }

  confirmAcknowledge(payload: ITimetableUpdateNode): void {
    payload.TimetableId = this.timetable.id
    if (payload.ScheduledTime) {
      this.onUpdateStopPointTime(payload)
    } else {
      this.acknowledgePayload = cloneDeep(payload)

      this.actionButton = 'updateStopPoint'
      this.openDialog({
        title: 'timetable.acknowledge_stop_point_change_message',
        content: 'timetable.normalize_process_message',
        buttonContent: 'timetable.process',
      })
    }
  }

  async onUpdateStopPointTime(payload: ITimetableUpdateNode) {
    this.loading = true
    try {
      await this.$store.dispatch(UPDATE_TIMETABLE_TIME, {
        payload,
        selectedDate: this.selectedDate,
      })
    } catch (error) {
      errorHandler(error)
    } finally {
      this.loading = false
    }
  }

  openDialog(dialogMessage: IDialogMessage) {
    this.dialog = {
      title: dialogMessage?.title ?? '',
      content: dialogMessage?.content ?? '',
      buttonContent: dialogMessage?.buttonContent ?? '',
      isVisible: true,
    }
  }

  clearDialogData() {
    this.actionButton = ''
    this.dialog = {
      title: '',
      content: '',
      buttonContent: '',
      isVisible: false,
    }
  }

  async onUpdateStopPoint(payload: ITimetableUpdateNode) {
    this.loading = true
    try {
      await this.$store.dispatch(ACKNOWNLEDGE_TIMETABLE, { payload, selectedDate: this.selectedDate })
    } catch (error) {
      errorHandler(error)
    } finally {
      this.loading = false
    }
  }

  created() {
    this.selectedDateObject = convertStringToDate(this.selectedDate)
  }

  buttonDeleteClicked() {
    this.actionButton = 'delete'
    this.openDialog({
      content: 'timetable.timetable_confirm_delete',
      buttonContent: 'timetable.process',
    })
  }
}
